import { isDateComplete } from "../dateAndTime";
import {
    validatePrimaryMemberDates, validateSpouseDates,
    validateAdditionalSpouseDates,
    validateChildBirthDate,
    validateCrossSpouseDates
} from "../dateValidationUtils";
import { isValidDate } from "./dateAndTime";
import {
    getPrimaryFallbackDob,
    getSpouseFallbackDob,
    isEditModeMinor,
} from "./memberAge";



export const validateDatesUtils = ({
    grandfatherData,
    grandmotherData,
    spouses,
    parentMember,
    setDateValidationErrors,
    isEditMode = false,
    editMemberData = null,
}) => {
    const errors = {
        primary: {
            dob: "",
            dod: "",
            weddingAnniversary: "",
        },
        spouse: {
            dob: "",
            dod: "",
            weddingAnniversary: "",
            divorce_date: "",
        },
        additional_spouse: [
            {
                dob: "",
                dod: "",
                weddingAnniversary: "",
                divorce_date: "",
            },
            // Additional spouses can be added here
        ],
    };


    const primaryFallbackDob = getPrimaryFallbackDob(editMemberData);
    const isPrimaryMinor = isEditModeMinor({
        isEditMode,
        dob: grandfatherData.dob,
        currentAge: grandfatherData.currentAge,
        fallbackDob: primaryFallbackDob,
    });

    // Validate primary DOB (required unless editing a member under 18)
    if (
        !isPrimaryMinor &&
        (!grandfatherData.dob ||
            !isValidDate(grandfatherData.dob) ||
            !isDateComplete(grandfatherData.dob))
    ) {
        errors.primary.dob = "Date of Birth is required.";
    }

    // Validate primary DOD if deceased
    if (grandfatherData.aliveStatus === "2") {
        if (!grandfatherData.dod || !isValidDate(grandfatherData.dod)) {
            errors.primary.dod = "Date of Death is required.";
        }
    }

    // Validate wedding anniversary if married (not In Relationship / Widowed)
    const isSingle = grandfatherData.maritalStatus == "1";
    const skipWeddingAnniversaryRequired = [3, 4].includes(
      Number(grandfatherData.maritalStatus)
    );
    if (!isSingle && !skipWeddingAnniversaryRequired) {
        if (
            !grandfatherData.weddingAnniversary ||
            !isValidDate(grandfatherData.weddingAnniversary)
        ) {
            // errors.primary.weddingAnniversary =
            //   "Wedding Anniversary is required. Please select month, day, and year.";
        }
    }

    // Chronological validation for primary member
    const primaryDateErrors = validatePrimaryMemberDates({
        dob: grandfatherData.dob,
        dod: grandfatherData.dod,
        weddingAnniversary: grandfatherData.weddingAnniversary,
    });
    // Merge chronological errors (don't overwrite required field errors)
    if (primaryDateErrors.dod && !errors.primary.dod) {
        errors.primary.dod = primaryDateErrors.dod;
    }
    if (primaryDateErrors.weddingAnniversary && !errors.primary.weddingAnniversary) {
        errors.primary.weddingAnniversary = primaryDateErrors.weddingAnniversary;
    }

    // Validate child birth date is not earlier than parent birth date (if parent data is available)
    if (
        parentMember &&
        parentMember.dob &&
        isDateComplete(parentMember.dob) &&
        grandfatherData.dob &&
        isDateComplete(grandfatherData.dob)
    ) {
        const childBirthError = validateChildBirthDate(
            grandfatherData.dob,
            parentMember.dob
        );
        if (childBirthError && !errors.primary.dob) {
            // Add to DOB error if no existing error
            errors.primary.dob = childBirthError;
        } else if (childBirthError) {
            // Append to existing error if there is one
            errors.primary.dob = `${errors.primary.dob} ${childBirthError}`;
        }
    }

    const spouseFallbackDob = getSpouseFallbackDob(editMemberData);
    const isSpouseMinor = isEditModeMinor({
        isEditMode,
        dob: grandmotherData.dob,
        currentAge: grandmotherData.currentAge,
        fallbackDob: spouseFallbackDob,
    });

    // Validate spouse dates if not single
    if (!isSingle) {
        // Check if primary has DOB but spouse doesn't
        if (
            !isSpouseMinor &&
            grandfatherData.dob &&
            isValidDate(grandfatherData.dob)
        ) {
            if (!grandmotherData.dob || !isValidDate(grandmotherData.dob)) {
                errors.spouse.dob = "Spouse Date of Birth is required";
            }
        }

        // Check if primary has DOD but spouse doesn't (if both are deceased)
        if (
            grandfatherData.aliveStatus === "2" &&
            grandmotherData.aliveStatus === "2"
        ) {
            if (grandfatherData.dod && isValidDate(grandfatherData.dod)) {
                if (!grandmotherData.dod || !isValidDate(grandmotherData.dod)) {
                    errors.spouse.dod =
                        "Spouse Date of Death is required when Primary Date of Death is provided.";
                }
            }
        }

        // Wedding anniversary should be same for both, but validate spouse if primary has it
        if (
            grandfatherData.weddingAnniversary &&
            isValidDate(grandfatherData.weddingAnniversary)
        ) {
            // Wedding anniversary is shared, so this is more of a consistency check
            // We'll validate that spouse DOB exists if wedding anniversary exists
            if (
                !isSpouseMinor &&
                (!grandmotherData.dob || !isValidDate(grandmotherData.dob))
            ) {
                errors.spouse.dob = "Spouse Date of Birth is required";
            }
        }

        // Divorce Date is now optional - validation removed
        // if (
        //     grandmotherData.divorce == 1 &&
        //     !isValidDate(grandmotherData.divorce_date)
        // ) {
        //     errors.spouse.divorce_date = "Spouse Divorce Date is required.";
        // }
        // Validate main spouse wedding anniversary if married (not In Relationship / Widowed)
        if (
          !skipWeddingAnniversaryRequired &&
          (
            !grandmotherData.weddingAnniversary ||
            !isValidDate(grandmotherData.weddingAnniversary) ||
            !isDateComplete(grandmotherData.weddingAnniversary)
          )
        ) {
            errors.spouse.weddingAnniversary =
                "Wedding Anniversary is required (MM/DD/YYYY).";
        }

        // Chronological validation for main spouse
        const spouseDateErrors = validateSpouseDates({
            dob: grandmotherData.dob,
            dod: grandmotherData.dod,
            weddingAnniversary: grandmotherData.weddingAnniversary,
            divorce_date: grandmotherData.divorce_date,
        });
        // Merge chronological errors (don't overwrite required field errors)
        if (spouseDateErrors.dod && !errors.spouse.dod) {
            errors.spouse.dod = spouseDateErrors.dod;
        }
        if (
            spouseDateErrors.weddingAnniversary &&
            !errors.spouse.weddingAnniversary
        ) {
            errors.spouse.weddingAnniversary = spouseDateErrors.weddingAnniversary;
        }
        if (
            spouseDateErrors.divorce_date &&
            !errors.spouse.divorce_date
        ) {
            errors.spouse.divorce_date = spouseDateErrors.divorce_date;
        }

        // Cross-validate with primary member dates (after individual spouse validation)
        if (spouses.length > 0 && spouses[0]) {
            const mainSpouse = spouses[0];
            const crossErrors = validateCrossSpouseDates(
                {
                    dob: grandfatherData.dob,
                    dod: grandfatherData.dod,
                    weddingAnniversary: grandfatherData.weddingAnniversary,
                },
                {
                    dob: mainSpouse.dob,
                    dod: mainSpouse.dod,
                    weddingAnniversary: mainSpouse.weddingAnniversary,
                    divorce_date: mainSpouse.divorce_date,
                }
            );

            // Merge cross-validation errors with existing primary errors (don't overwrite existing errors)
            if (crossErrors.primary.dob && !errors.primary.dob) {
                errors.primary.dob = crossErrors.primary.dob;
            }
            if (crossErrors.primary.dod && !errors.primary.dod) {
                errors.primary.dod = crossErrors.primary.dod;
            }
            if (crossErrors.primary.weddingAnniversary && !errors.primary.weddingAnniversary) {
                errors.primary.weddingAnniversary = crossErrors.primary.weddingAnniversary;
            }

            // Merge cross-validation errors with existing spouse errors (don't overwrite existing errors)
            if (crossErrors.spouse.dob && !errors.spouse.dob) {
                errors.spouse.dob = crossErrors.spouse.dob;
            }
            if (crossErrors.spouse.dod && !errors.spouse.dod) {
                errors.spouse.dod = crossErrors.spouse.dod;
            }
            if (crossErrors.spouse.weddingAnniversary && !errors.spouse.weddingAnniversary) {
                errors.spouse.weddingAnniversary = crossErrors.spouse.weddingAnniversary;
            }
            if (crossErrors.spouse.divorce_date && !errors.spouse.divorce_date) {
                errors.spouse.divorce_date = crossErrors.spouse.divorce_date;
            }
        }

        // Initialize errors.additional_spouse based on the number of additional spouses
        errors.additional_spouse = spouses.slice(1).map(() => ({
            dob: "",
            dod: "",
            divorce_date: "",
            wedding_anniversary: "",
        }));

        // Validate additional spouses
        spouses.slice(1).forEach((spouse, index) => {
            const isAdditionalSpouseMinor = isEditModeMinor({
                isEditMode,
                dob: spouse.dob,
                currentAge: spouse.currentAge,
                fallbackDob: spouse.originalDob || spouse.dob,
            });

            // Additional spouse DOB validation
            if (
                !isAdditionalSpouseMinor &&
                (!spouse.dob || !isDateComplete(spouse.dob))
            ) {
                errors.additional_spouse[index].dob = `Additional Spouse ${index + 1} Date of Birth is required.`;
            }

            // Additional spouse DOD validation if deceased
            if (spouse.aliveStatus === "2") {
                if (!spouse.dod || !isDateComplete(spouse.dod)) {
                    errors.additional_spouse[index].dod = `Additional Spouse ${index + 1} Date of Death is required.`;
                }
            }

            // Divorce Date is now optional - validation removed
            // if (spouse.divorce == 1) {
            //     if (!spouse.divorce_date || !isDateComplete(spouse.divorce_date)) {
            //         errors.additional_spouse[index].divorce_date = `Additional Spouse ${index + 1} Divorce Date is required.`;
            //     }
            // }
            // Validate additional spouse wedding anniversary if married (not In Relationship / Widowed)
            if (
                !skipWeddingAnniversaryRequired &&
                (
                    !spouse.wedding_anniversary ||
                    !isValidDate(spouse.wedding_anniversary)
                )
            ) {
                errors.additional_spouse[index].wedding_anniversary = `Additional Spouse ${index + 1} Wedding Anniversary is required.`;
            }

            // Chronological validation for additional spouse
            const additionalSpouseDateErrors = validateAdditionalSpouseDates({
                dob: spouse.dob,
                dod: spouse.dod,
                weddingAnniversary: spouse.wedding_anniversary,
                divorce_date: spouse.divorce_date,
            });
            // Merge chronological errors (don't overwrite required field errors)
            if (
                additionalSpouseDateErrors.dod &&
                !errors.additional_spouse[index].dod
            ) {
                errors.additional_spouse[index].dod =
                    additionalSpouseDateErrors.dod;
            }
            if (
                additionalSpouseDateErrors.weddingAnniversary &&
                !errors.additional_spouse[index].wedding_anniversary
            ) {
                errors.additional_spouse[index].wedding_anniversary =
                    additionalSpouseDateErrors.weddingAnniversary;
            }
            if (
                additionalSpouseDateErrors.divorce_date &&
                !errors.additional_spouse[index].divorce_date
            ) {
                errors.additional_spouse[index].divorce_date =
                    additionalSpouseDateErrors.divorce_date;
            }

            // Cross-validate with primary member dates
            const crossErrors = validateCrossSpouseDates(
                {
                    dob: grandfatherData.dob,
                    dod: grandfatherData.dod,
                    weddingAnniversary: grandfatherData.weddingAnniversary,
                },
                {
                    dob: spouse.dob,
                    dod: spouse.dod,
                    weddingAnniversary: spouse.wedding_anniversary,
                    divorce_date: spouse.divorce_date,
                }
            );

            // Merge cross-validation errors with existing primary errors (don't overwrite existing errors)
            if (crossErrors.primary.dob && !errors.primary.dob) {
                errors.primary.dob = crossErrors.primary.dob;
            }
            if (crossErrors.primary.dod && !errors.primary.dod) {
                errors.primary.dod = crossErrors.primary.dod;
            }
            if (crossErrors.primary.weddingAnniversary && !errors.primary.weddingAnniversary) {
                errors.primary.weddingAnniversary = crossErrors.primary.weddingAnniversary;
            }

            // Merge cross-validation errors with existing additional spouse errors (don't overwrite existing errors)
            if (crossErrors.spouse.dob && !errors.additional_spouse[index].dob) {
                errors.additional_spouse[index].dob = crossErrors.spouse.dob;
            }
            if (crossErrors.spouse.dod && !errors.additional_spouse[index].dod) {
                errors.additional_spouse[index].dod = crossErrors.spouse.dod;
            }
            if (crossErrors.spouse.weddingAnniversary && !errors.additional_spouse[index].wedding_anniversary) {
                errors.additional_spouse[index].wedding_anniversary = crossErrors.spouse.weddingAnniversary;
            }
            if (crossErrors.spouse.divorce_date && !errors.additional_spouse[index].divorce_date) {
                errors.additional_spouse[index].divorce_date = crossErrors.spouse.divorce_date;
            }
        });
    }

    setDateValidationErrors(errors);
    console.log("Date errors : ", errors);
    return (
        Object.values(errors.primary).every((err) => !err) &&
        Object.values(errors.spouse).every((err) => !err) &&
        errors.additional_spouse.every((spouseError) =>
            Object.values(spouseError).every((err) => !err)
        )
    );
};